import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { LoadingScreen } from "@/components/LoadingScreen";
import { useNews } from "@/hooks/useNews";
import type { NewsItem } from "@/types/news.types";
import { NewsCard } from "./NewsCard";

interface NewsListProps {
  onNewsSelect: (item: NewsItem) => void;
}

function NewsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-[20px] p-3"
          style={{ boxShadow: "inset 0 0 0 1px var(--c-line)" }}
        >
          <div className="skeleton size-[72px] shrink-0 rounded-2xl" />
          <div className="flex-1 space-y-2">
            <div className="skeleton h-3 w-20 rounded-full" />
            <div className="skeleton h-4 w-full rounded-lg" />
            <div className="skeleton h-4 w-3/5 rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function NewsList({ onNewsSelect }: NewsListProps) {
  const { t } = useTranslation();
  const { news, isLoading, isLoadingMore, hasMore, loadMore, error } = useNews();

  if (isLoading) {
    return (
      <div className="mt-4">
        <NewsSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-10 flex flex-col items-center justify-center gap-3 text-center px-4">
        <span className="text-4xl">⚠️</span>
        <p style={{ color: "var(--c-text-muted)" }} className="text-sm">
          {t("errorLoading")}
        </p>
      </div>
    );
  }

  if (!isLoading && news.length === 0) {
    return (
      <div className="mt-16 flex flex-col items-center justify-center gap-3 text-center px-4">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center"
          style={{ background: "var(--c-brand-tint)" }}
        >
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" style={{ color: "var(--primary)" }} stroke="currentColor" strokeWidth={1.5} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
        </div>
        <p className="font-semibold" style={{ color: "var(--c-text)" }}>
          {t("newsEmpty")}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="grid grid-cols-1 gap-4">
        {news.map((item, index) => (
          <NewsCard key={item.id} item={item} index={index} onClick={onNewsSelect} />
        ))}
      </div>

      {hasMore && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-5 pb-2"
        >
          <button
            type="button"
            onClick={() => loadMore()}
            disabled={isLoadingMore}
            className="w-full py-3 rounded-2xl font-semibold text-sm transition-all active:scale-95"
            style={{
              background: isLoadingMore ? "var(--c-elevated-1)" : "var(--c-brand-tint)",
              color: isLoadingMore ? "var(--c-text-muted)" : "var(--primary)",
              border: "1px solid var(--c-brand-tint-strong)",
            }}
          >
            {isLoadingMore ? (
              <span className="flex items-center justify-center gap-2">
                <LoadingScreen className="min-h-0! p-0! w-4 h-4" />
                {t("loading")}
              </span>
            ) : (
              t("loadMore")
            )}
          </button>
        </motion.div>
      )}
    </div>
  );
}
